import React, { useCallback, useEffect, useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useAuthStore, useModalStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useBillingPalette } from '@/components/Settings/billing/BillingGroup';
import { AUTO_TOPUP_BONUS_PERCENT, canNudgeAutoTopup } from '@/lib/autoTopupOffer';
import { trackEvent } from '@/constants/utils/analytics';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const isWeb = Platform.OS === 'web';

const createStyles = (palette, compact) => StyleSheet.create({
  card: {
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: palette.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    backgroundColor: palette.isDark ? 'rgba(255,255,255,0.05)' : (palette.colors?.solid_color_primary || '#fff'),
    padding: compact ? 14 : 18,
    gap: compact ? 12 : 16,
    ...(isWeb && !palette.isDark
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 10px 28px -14px rgba(0,0,0,0.16)' }
      : null),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  tile: {
    width: compact ? 34 : 40,
    height: compact ? 34 : 40,
    borderRadius: compact ? 9 : 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.ink,
    flexShrink: 0,
  },
  copy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  bonus: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: palette.tileFill,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  cta: {
    height: compact ? 34 : 38,
    paddingHorizontal: compact ? 14 : 18,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.ink,
  },
  footnote: {
    flex: 1,
    minWidth: 140,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: palette.isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
    backgroundColor: palette.isDark ? 'rgba(255,255,255,0.05)' : (palette.colors?.solid_color_primary || '#fff'),
  },
  rowTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
});

/**
 * Contextual invitation to turn on auto top-up, shown where people run out of
 * or buy credits. It only opens the configuration screen; nothing is enabled
 * or charged from here.
 */
function AutoTopUpNudge({ variant = 'card', compact = false, source = 'unknown', onBeforeAction, title, message }) {
  const palette = useBillingPalette();
  const styles = useMemo(() => createStyles(palette, compact), [palette, compact]);
  const user = useAuthStore(state => state.user);
  const { openModal } = useModalStore(state => state.actions);
  const { usesWhopCheckout } = useGabPurchase();
  const visible = canNudgeAutoTopup({ user, usesWhopCheckout });

  useEffect(() => {
    if (visible) trackEvent('conversion', 'auto_topup_nudge_shown', source);
  }, [visible, source]);

  const handlePress = useCallback(() => {
    trackEvent('conversion', 'auto_topup_nudge_clicked', source);
    onBeforeAction?.();
    openModal('AutoTopUpConfigModal');
  }, [onBeforeAction, openModal, source]);

  if (!visible) return null;

  const bonusLabel = `+${AUTO_TOPUP_BONUS_PERCENT}% credits`;
  const pressFeedback = ({ hovered, pressed }) => ({
    opacity: pressed ? 0.8 : hovered ? 0.9 : 1,
    ...(isWeb ? { cursor: 'pointer', transition: 'opacity 120ms ease' } : null),
  });

  if (variant === 'row') {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Turn on auto top-up and get ${AUTO_TOPUP_BONUS_PERCENT}% more credits`}
        onPress={handlePress}
        style={state => [styles.row, pressFeedback(state)]}
      >
        <View style={[styles.tile, { width: 32, height: 32, borderRadius: 9 }]}>
          <Icon name="arrows-rotate" size={13} color={palette.inkInverse} />
        </View>
        <View style={styles.copy}>
          <View style={styles.titleRow}>
            <Text size="s" weight="semibold" color="primary">{title || 'Auto top-up'}</Text>
            <View style={styles.bonus}>
              <Text size="xxs" weight="bold" color="primary">{bonusLabel}</Text>
            </View>
          </View>
          <Text size="xs" color="secondary">
            {message || 'Refills automatically when you run low, with 10% extra every time.'}
          </Text>
        </View>
        <View style={styles.rowTrailing}>
          <Text size="s" weight="medium" color="primary">Turn on</Text>
          <Icon name="chevron-right" size={11} color={palette.colors?.text_color_tertiary} />
        </View>
      </Pressable>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.tile}>
          <Icon name="arrows-rotate" size={compact ? 14 : 16} color={palette.inkInverse} />
        </View>
        <View style={styles.copy}>
          <View style={styles.titleRow}>
            <Text size={compact ? 's' : 'n'} weight="semibold" color="primary">
              {title || 'Never stop mid-task'}
            </Text>
            <View style={styles.bonus}>
              <Text size="xxs" weight="bold" color="primary">{bonusLabel}</Text>
            </View>
          </View>
          <Text size={compact ? 'xs' : 's'} color="secondary">
            {message || `Auto top-up refills your credits the moment you run low, and adds ${AUTO_TOPUP_BONUS_PERCENT}% extra to every pack.`}
          </Text>
        </View>
      </View>
      <View style={styles.footer}>
        <Pressable
          accessibilityRole="button"
          onPress={handlePress}
          style={state => [styles.cta, pressFeedback(state)]}
        >
          <Text size="s" weight="semibold" style={{ color: palette.inkInverse }}>Turn on auto top-up</Text>
        </Pressable>
        <Text size="xs" color="secondary" style={styles.footnote}>
          You choose the pack and a monthly limit. Turn it off anytime.
        </Text>
      </View>
    </View>
  );
}

AutoTopUpNudge.propTypes = {
  variant: PropTypes.oneOf(['card', 'row']),
  compact: PropTypes.bool,
  source: PropTypes.string,
  onBeforeAction: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
};

export default React.memo(AutoTopUpNudge);
